import { supabase } from './supabaseClient'

// Longest-edge cap for a chat photo. 1280px keeps it crisp full-bleed
// on a phone while landing most shots under ~300 KB at q=0.82.
const MAX_EDGE = 1280
const JPEG_QUALITY = 0.82

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err) }
    img.src = url
  })
}

// Downscale to fit within MAX_EDGE on the longest side, preserving
// aspect ratio (no crop — chat photos shouldn't be squared), encode
// as JPEG.
async function processToJpeg(file) {
  const img = await loadImage(file)
  const { naturalWidth: w, naturalHeight: h } = img
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h))
  const outW = Math.round(w * scale)
  const outH = Math.round(h * scale)

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, outW, outH)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null')),
      'image/jpeg',
      JPEG_QUALITY,
    )
  })
}

// Upload a chat image for the sender. Writes to
// message-images/<senderId>/<uuid>.jpg and returns the public URL.
// Resolves { url, error }.
export async function uploadMessageImage(senderId, file) {
  if (!senderId) return { url: null, error: 'missing senderId' }
  if (!file) return { url: null, error: 'missing file' }

  let blob
  try {
    blob = await processToJpeg(file)
  } catch (e) {
    return { url: null, error: e?.message || 'image decode failed' }
  }

  const id = (crypto?.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  const path = `${senderId}/${id}.jpg`
  const { error: uploadError } = await supabase.storage
    .from('message-images')
    .upload(path, blob, { upsert: false, contentType: 'image/jpeg', cacheControl: '3600' })
  if (uploadError) return { url: null, error: uploadError.message }

  const { data: pub } = supabase.storage.from('message-images').getPublicUrl(path)
  if (!pub?.publicUrl) return { url: null, error: 'no publicUrl returned' }
  return { url: pub.publicUrl, error: null }
}
